// HTTP DTOs for apps/api ↔ apps/web. docs/contracts.md is the prose version;
// keep both in the same commit. All JSON is snake_case; timestamps are ISO
// strings; meeting-timeline values are integer ms.
import type {
  IntentState,
  MeetingStatus,
  RecordingMode,
  RecordingUnavailableReason,
  RoomVisibility,
  SpeakerKind,
} from './enums.js';
import type { Json, Uuid } from './db.js';

// ── Errors ────────────────────────────────────────────────────────────────

export const ERROR_CODES = {
  invalid_request: 400,
  unauthenticated: 401,
  /** Signed in but not an active, mapped staff member. */
  not_staff: 403,
  /** Staff, but not allowed this action (e.g. deleting someone else's meeting). */
  forbidden: 403,
  /** Unknown room, or a private room the caller cannot see. */
  room_not_found: 404,
  /** Unknown, deleted or unreadable meeting: one answer, no existence leak. */
  meeting_not_found: 404,
  rate_limited: 429,
  internal: 500,
  /** LiveKit or Supabase unreachable. */
  upstream_unavailable: 503,
} as const;
export type ErrorCode = keyof typeof ERROR_CODES;

export interface ErrorBody {
  error: {
    code: ErrorCode;
    /** pt-BR, safe to show to the user. */
    message: string;
    request_id: string;
  };
}

// ── Timelines ─────────────────────────────────────────────────────────────

/**
 * `meeting`: start/end are on the meeting timeline. `track`: the microphone
 * has no measured track_media_offset_ms yet, so the values are relative to
 * that microphone only; never place them on the meeting timeline or
 * interleave them with other tracks.
 */
export type Timeline = 'meeting' | 'track';

// ── Pagination ────────────────────────────────────────────────────────────

/** Query: `?limit=<1..100, default 20>&cursor=<opaque>`. */
export interface PageQuery {
  limit?: number;
  cursor?: string;
}

export interface Page<T> {
  items: T[];
  /** Opaque; null on the last page. */
  next_cursor: string | null;
}

// ── Shared pieces ─────────────────────────────────────────────────────────

export interface PersonRef {
  person_id: Uuid;
  display_name: string;
}

/** Authoritative recording status for a meeting; the UI never guesses it. */
export interface RecordingStatus {
  mode: RecordingMode;
  /** Null when mode = recorded. */
  unavailable_reason: RecordingUnavailableReason | null;
}

/**
 * Recording status of a LIVE meeting (token, presence, start): `can_start`
 * says whether "Gravar" may be pressed now (not recording, recorder
 * available, scratch ok, slot free). The server decides again on the press.
 */
export interface LiveRecordingStatus extends RecordingStatus {
  can_start: boolean;
}

// ── POST /token ───────────────────────────────────────────────────────────

export interface TokenRequest {
  room_slug: string;
  /** Pre-join toggle; stored as server-controlled participant metadata. */
  shared_mic: boolean;
}

export interface TokenResponse {
  /** wss URL of the LiveKit server. */
  server_url: string;
  token: string;
  expires_at: string;
  identity: string;
  room: { slug: string; title: string };
  meeting_id: Uuid;
  recording: LiveRecordingStatus;
}

// ── POST /meetings/:id/recording/start ────────────────────────────────────

export const RECORDING_START_OUTCOMES = ['started', 'already_recording', 'unavailable'] as const;
export type RecordingStartOutcome = (typeof RECORDING_START_OUTCOMES)[number];

/**
 * Always 200 for a live meeting the caller is in. `unavailable`: the start
 * was refused; `recording.unavailable_reason` is why (the current gate).
 */
export interface RecordingStartResponse {
  outcome: RecordingStartOutcome;
  recording: LiveRecordingStatus;
}

// ── GET /rooms/presence ───────────────────────────────────────────────────

export interface RoomPresence {
  slug: string;
  title: string;
  /**
   * `ok`: participants is the current list (empty means "vazia").
   * `unavailable`: presence could not be read; participants is empty and the
   * UI shows "presença indisponível", never "vazia".
   */
  status: 'ok' | 'unavailable';
  /** Staff only; recorder and non-person identities filtered out. */
  participants: PersonRef[];
  recording: LiveRecordingStatus | null;
}

export interface PresenceResponse {
  rooms: RoomPresence[];
  observed_at: string;
  /** Upper bound on data age (1000 ms in v1). */
  max_age_ms: number;
}

// ── GET /meetings (recent) ────────────────────────────────────────────────

export interface MeetingListItem {
  id: Uuid;
  room: { slug: string; title: string } | null;
  status: MeetingStatus;
  visibility: RoomVisibility;
  recording: RecordingStatus;
  started_at: string | null;
  ended_at: string | null;
  participants: PersonRef[];
  has_summary: boolean;
}

export type MeetingListResponse = Page<MeetingListItem>;

// ── GET /meetings/:id ─────────────────────────────────────────────────────

export interface TrackStatus {
  intent_id: Uuid;
  participant: PersonRef | null;
  shared_mic: boolean;
  state: IntentState;
  timeline: Timeline;
}

export interface MeetingDetailResponse {
  meeting: MeetingListItem & { owner_person_id: Uuid | null; can_delete: boolean };
  tracks: TrackStatus[];
  summary: SummaryView | null;
}

export interface SummaryView {
  model: string;
  prompt_version: string;
  transcription_version: number;
  created_at: string;
  /** Raw LLM JSON; render generically as bounded plain text. */
  body: Json;
}

// ── GET /meetings/:id/segments ────────────────────────────────────────────

export interface SegmentView {
  id: Uuid;
  speaker_kind: SpeakerKind;
  /** Null for shared_mic; the UI shows the shared-mic label. */
  speaker: PersonRef | null;
  start_ms: number;
  end_ms: number;
  text: string;
  intent_id: Uuid;
  timeline: Timeline;
}

export type SegmentsResponse = Page<SegmentView>;

// ── GET /search?q= ────────────────────────────────────────────────────────

export interface SearchHit {
  kind: 'segment' | 'summary';
  meeting_id: Uuid;
  meeting_started_at: string | null;
  room_slug: string | null;
  /** Segment hits only. */
  segment_id: Uuid | null;
  start_ms: number | null;
  speaker_kind: SpeakerKind | null;
  speaker: PersonRef | null;
  /** Plain text; the UI never renders it as HTML. */
  snippet: string;
  rank: number;
  /** Segment hits only; null for summary hits. */
  intent_id: Uuid | null;
  timeline: Timeline | null;
}

export type SearchResponse = Page<SearchHit>;

// ── DELETE /meetings/:id ──────────────────────────────────────────────────

export interface DeleteMeetingResponse {
  meeting_id: Uuid;
  deleted_at: string;
  /** Cleanup continues in a `delete` job. */
  status: 'deleting';
}
